import {
  getComparator,
  getNestedValue,
  removeAccents,
  replaceSpaceWithCharacter,
  stableSort,
} from "app/services/Features";
import { FC, ReactNode, useEffect, useState } from "react";
import { useTheme } from "styled-components";
import Table, { Order } from "./Table";
import Box from "./Box";
import TableToolBar from "./TableToolBar";
import Card from "./Card";
import TablePagination from "./TablePagination";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { ThemeType } from "app/types/theme";

interface DataTableProps {
  headColumns: any[];
  rows: any[];
  loading: boolean;
  formatRows: any;
  translate: any;
  placeholderSearch?: string;
  onRefresh?: () => void;
  titleTable?: string;
  router?: string;
  otherFilters?: ReactNode;
  additionalButtonsContainer?: ReactNode;
  showToolBar?: boolean;
  stickyToolBar?: boolean;
  stickyToolBarTop?: number | string;
}

interface Data {
  acoes: any;
  carbstestResultId: any;
}

interface HeadCell {
  disablePadding?: boolean;
  id?: keyof Data | any;
  label: string;
  numeric?: boolean;
}

interface MountedTableHeaderProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  headColumns?: HeadCell[];
}

const DataTable: FC<DataTableProps> = ({
  headColumns,
  rows,
  loading,
  translate,
  formatRows,
  placeholderSearch,
  router,
  onRefresh,
  titleTable,
  otherFilters,
  additionalButtonsContainer,
  showToolBar = true,
  stickyToolBar = false,
  stickyToolBarTop = 0,
}) => {
  const [resultsSearch, setResultsSearch] = useState<any[]>([]);

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState("");

  const theme: ThemeType = useTheme() as ThemeType;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = resultsSearch.map((n: any) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const searchInData = () => {
    const filteredRows = rows.filter((row: any) => {
      for (let i = 0; i < headColumns.length; i++) {
        const column = headColumns[i];
        const columnName: string = column.id;
        const cellValue = removeAccents(
          String(getNestedValue(row, columnName)),
        );
        const value = removeAccents(search);

        if (cellValue && cellValue.includes(value)) {
          return true;
        }
      }
      return false;
    });

    return filteredRows;
  };

  useEffect(() => {
    const filteredRows = searchInData();

    setResultsSearch(filteredRows);

    const newRows = stableSort(
      filteredRows,
      getComparator(order, orderBy),
    ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    setResults(newRows);
  }, [rows, search, order, orderBy, page, rowsPerPage]);

  const MountedTableHeader = (props: MountedTableHeaderProps) => {
    const { order, orderBy, onRequestSort, headColumns } = props;
    const createSortHandler = (property: keyof Data) => (
      event: React.MouseEvent<unknown>,
    ) => {
      onRequestSort(event, property);
    };

    return (
      <Table.Head>
        <Table.Row>
          {headColumns?.map(headCell => (
            <Table.Cell
              key={
                headCell.id !== "none"
                  ? headCell.id
                  : `${replaceSpaceWithCharacter(
                      removeAccents(headCell.label),
                      "-",
                    )}-${headCell.id}`
              }
              style={{
                padding: headCell.disablePadding ? "0" : "20px",
              }}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.id !== "none" ? (
                <Table.SortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell?.id)}
                >
                  {headCell.label}
                </Table.SortLabel>
              ) : (
                headCell.label
              )}
            </Table.Cell>
          ))}
        </Table.Row>
      </Table.Head>
    );
  };

  return (
    <Box
      style={{
        width: "100%",
        boxShadow: "0px 0px 20px #00000021",
        padding: "0",
        display: "flex",
        flexDirection: "column",
        overflow: stickyToolBar ? "visible" : "hidden",
        backgroundColor:
          theme.palette.mode === "light"
            ? theme.background
            : theme.secondaryBackground,
      }}
    >
      {showToolBar && (
        <div
          style={
            stickyToolBar
              ? {
                  position: "sticky",
                  top: stickyToolBarTop,
                  zIndex: 20,
                  width: "100%",
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? theme.background
                      : theme.secondaryBackground,
                }
              : undefined
          }
        >
          <TableToolBar
            onRefresh={onRefresh}
            router={router}
            titleTable={titleTable}
            placeholderSearch={placeholderSearch}
            search={search}
            setSearch={setSearch}
            additionalButtonsContainer={additionalButtonsContainer}
          />
        </div>
      )}

      {otherFilters && otherFilters}
      <Table>
        <MountedTableHeader
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={resultsSearch.length}
          headColumns={headColumns}
        />
        <Table.Body>
          {loading ? (
            <Table.Row>
              <Table.Cell colSpan={headColumns.length} className="text-center">
                {translate("defaultMessages.loading")}
              </Table.Cell>
            </Table.Row>
          ) : results && results.length > 0 ? (
            results.map((row: any) => {
              return formatRows(row, `enhanced-table-checkbox-`);
            })
          ) : (
            <Table.Row>
              <Table.Cell colSpan={headColumns.length} className="text-center">
                {translate("datatable.nothingData")}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      <Card
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "end",
          padding: "0",
          margin: "0",
          borderRadius: "0",
          border: "none",
        }}
      >
        <TablePagination
          labelRowsPerPage={translate("datatable.labelRowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${translate("datatable.of")} ${count}`
          }
          rowsPerPageOptions={[5, 10, 25]}
          count={resultsSearch.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            icon: <ChevronLeft />,
          }}
          nextIconButtonProps={{
            icon: <ChevronRight />,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </Box>
  );
};

export default DataTable;
